import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, ExternalLink, RefreshCw, Loader2, Clock
} from "lucide-react";

const HEALTH_DOT = {
  healthy: "bg-status-green", stale: "bg-gold", offline: "bg-status-red",
  down: "bg-status-red", auth_error: "bg-status-red", not_configured: "bg-zinc-600",
};
const HEALTH_LABEL = {
  healthy: "live", stale: "stale data", offline: "offline",
  down: "unreachable", auth_error: "auth error", not_configured: "not configured",
};

function formatMetricValue(metric) {
  if (metric.unit === "cents") return `$${(metric.value / 100).toFixed(2)}`;
  if (metric.unit === "percent") return `${metric.value}%`;
  if (metric.unit === "seconds") return `${metric.value}s`;
  return metric.value.toLocaleString();
}

function BrandCard({ data }) {
  const { brand, health, cached, color } = data;
  const summary = data.data;
  const metrics = summary?.metrics || [];
  const headline = summary?.headline || "";
  const alerts = data.alerts || [];
  const alertCount = alerts.length;

  return (
    <div className="bg-studio-raised border border-[#2a2e36] rounded-xl overflow-hidden brand-card" data-testid={`integration-${brand?.id}`}>
      <div className="flex items-center gap-3 p-4 border-b border-[#222630]">
        <div className="w-3 h-3 rounded-full" style={{ background: color || "#71717a" }} />
        <h3 className="text-sm font-semibold text-zinc-100">{brand?.name}</h3>
        {alertCount > 0 && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/10 text-gold text-[10px] font-mono">
            <AlertTriangle size={10} /> {alertCount}
          </span>
        )}
        <div className="flex items-center gap-1.5 ml-auto">
          <span className={`w-2 h-2 rounded-full ${HEALTH_DOT[health] || "bg-zinc-600"}`} />
          <span className="text-[10px] font-mono text-zinc-500">{HEALTH_LABEL[health] || health}</span>
          {cached && <span className="text-[9px] font-mono text-zinc-600">(cached)</span>}
        </div>
      </div>

      {headline && (
        <div className="px-4 py-3 border-b border-[#222630]">
          <p className="text-sm text-zinc-300">{headline}</p>
        </div>
      )}

      {metrics.length > 0 ? (
        <div className="grid grid-cols-3 gap-px bg-[#222630]">
          {metrics.slice(0, 6).map((m) => (
            <div key={m.key} className="bg-studio-raised p-3">
              <p className="text-[10px] text-zinc-500 uppercase font-mono tracking-wider truncate">{m.label}</p>
              <div className="flex items-baseline gap-1.5 mt-1">
                <span className="stat-number text-base text-zinc-100">{formatMetricValue(m)}</span>
                {m.change_direction && m.change_pct != null && (
                  <span className={`text-[10px] font-mono ${
                    m.change_direction === "up" ? (m.key.includes("churn") || m.key.includes("cost") ? "text-status-red" : "text-status-green")
                    : m.change_direction === "down" ? (m.key.includes("churn") || m.key.includes("cost") ? "text-status-green" : "text-status-red")
                    : "text-zinc-500"
                  }`}>
                    {m.change_direction === "up" ? "+" : ""}{m.change_pct}%
                  </span>
                )}
              </div>
              {m.period && <p className="text-[9px] text-zinc-600 font-mono mt-0.5">{m.period.replace("vs_", "vs ")}</p>}
            </div>
          ))}
        </div>
      ) : health === "down" || health === "not_configured" ? (
        <div className="px-4 py-8 text-center">
          <p className="text-xs text-zinc-500">{health === "not_configured" ? "Add API key to connect" : "Unable to reach app"}</p>
        </div>
      ) : null}

      {/* Alerts — capped height, scrollable */}
      {alerts.length > 0 && (
        <div className="border-t border-[#222630]">
          <div className="px-4 py-2 border-b border-[#222630]">
            <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">{alerts.length} alert{alerts.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="max-h-[120px] overflow-y-auto">
            {alerts.map((alert) => (
            <div key={alert.id} className="flex items-start gap-2.5 px-4 py-2.5 border-b border-[#222630] last:border-0">
              <AlertTriangle size={12} className={alert.severity === "critical" ? "text-status-red mt-0.5" : alert.severity === "warning" ? "text-gold mt-0.5" : "text-zinc-500 mt-0.5"} />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-zinc-300">{alert.title}</p>
                <p className="text-[10px] text-zinc-500 truncate">{alert.detail}</p>
              </div>
              {alert.action_url && (
                <a href={alert.action_url} target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-gold transition-colors flex-shrink-0">
                  <ExternalLink size={12} />
                </a>
              )}
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EventItem({ event }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-[#222630] last:border-0">
      <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: event.brand_color || "#71717a" }} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zinc-300">{event.title}</p>
        {event.detail && <p className="text-xs text-zinc-500 truncate">{event.detail}</p>}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-[10px] text-zinc-600 font-mono">{event.brand_name}</p>
        <p className="text-[10px] text-zinc-600 font-mono">{event.created_at ? new Date(event.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [summaries, setSummaries] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (showRefresh) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [sumRes, alertRes, evtRes] = await Promise.all([
        api.get("/integrations/summary"),
        api.get("/integrations/alerts"),
        api.get("/integrations/events?limit=15"),
      ]);

      // Attach alerts to their brand summaries
      const alertsByBrand = {};
      for (const a of alertRes.data.alerts || []) {
        if (!alertsByBrand[a.brand_id]) alertsByBrand[a.brand_id] = [];
        alertsByBrand[a.brand_id].push(a);
      }
      const enriched = (sumRes.data.summaries || []).map((s) => ({
        ...s,
        alerts: alertsByBrand[s.brand?.id] || [],
      }));

      setSummaries(enriched);
      setAlerts(alertRes.data.alerts || []);
      setEvents(evtRes.data.events || []);
    } catch { toast.error("Failed to load integrations"); }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => { fetchData(false); }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner-gold" /></div>;

  const totalAlerts = alerts.filter((a) => a.severity === "warning" || a.severity === "critical").length;

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-zinc-100">Dashboard</h1>
          <p className="section-label mt-2">// app integrations</p>
        </div>
        <div className="flex items-center gap-3">
          {totalAlerts > 0 && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/10 text-gold text-xs font-mono">
              <AlertTriangle size={13} /> {totalAlerts} alert{totalAlerts !== 1 ? "s" : ""}
            </span>
          )}
          <button onClick={() => fetchData(true)} disabled={refreshing}
            className="p-2 rounded-lg border border-[#363b45] text-zinc-500 hover:text-gold hover:border-gold/30 transition-colors disabled:opacity-40"
            data-testid="refresh-dashboard">
            {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          </button>
        </div>
      </div>

      {summaries.length === 0 ? (
        <div className="text-center py-16">
          <Activity size={40} className="text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">No app integrations configured yet</p>
          <p className="section-label mt-2">// add API keys in railway to connect your apps</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 stagger-children">
          {summaries.map((s) => (
            <BrandCard key={s.brand?.id} data={s} />
          ))}
        </div>
      )}

      {/* Events feed */}
      {events.length > 0 && (
        <div>
          <h2 className="font-display text-xl font-semibold text-zinc-100 mb-1">Recent Events</h2>
          <p className="section-label mb-4">// across all apps</p>
          <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-4">
            {events.map((e) => <EventItem key={e.id} event={e} />)}
          </div>
        </div>
      )}
    </div>
  );
}
